/**
 * AuthShell — split-screen layout shared by Login / Register / ForgotPassword.
 *
 * Design Refresh Phase 2: replaces the old centered-card auth layout with
 * Modernize's two-column pattern:
 *   - Left 55%: animated illustration panel (hidden on small viewports)
 *   - Right 45%: form content (children)
 *
 * The illustration is our own — aurora gradient mesh + animated SVG shapes
 * + brand lockup + rotating tagline. No vendored assets.
 *
 * Motion: logo scale-in, tagline fade-up, form staggered entrance.
 * Respects useReducedMotion.
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Boxes } from "lucide-react";

import { AuthIllustration } from "./AuthIllustration";
import {
  revealFromBelow,
  staggerCards,
  staggerCardItem,
  springIn,
  useMotionVariants,
} from "@/lib/animations";
import { cn } from "@/lib/utils";

export interface AuthShellProps {
  /** Small eyebrow above the title ("Sign in", "Create account", etc.) */
  eyebrow?: string;
  title: string;
  subtitle?: string;
  /** Form / action content */
  children: ReactNode;
  /** Bottom link (e.g., "Don't have an account? Sign up") */
  footer?: ReactNode;
  /** Show the Beta badge next to the FTL wordmark. */
  showBeta?: boolean;
  /** Tagline rendered inside the illustration panel. Defaults to the
   * product pitch. Short sentences; 2-3 lines max. */
  illustrationTagline?: string;
}

export function AuthShell({
  eyebrow,
  title,
  subtitle,
  children,
  footer,
  showBeta = true,
  illustrationTagline = "AI-led program management. Decompose. Verify. Ship — with audit trail.",
}: AuthShellProps) {
  const reducedMotion = useReducedMotion();
  const reveal = useMotionVariants(revealFromBelow);
  const stagger = useMotionVariants(staggerCards);
  const staggerItem = useMotionVariants(staggerCardItem);
  const spring = useMotionVariants(springIn);

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-[55%_45%]">
      {/* Left — illustration panel (hidden on mobile) */}
      <div className="relative hidden overflow-hidden bg-gradient-to-br from-brand-900 via-brand-800 to-slate-900 lg:block">
        <AuthIllustration />

        <div className="relative z-10 flex h-full flex-col justify-between p-10 xl:p-14">
          <motion.div
            variants={spring}
            initial={reducedMotion ? false : "hidden"}
            animate="visible"
            className="flex items-center gap-3"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 shadow-[var(--shadow-glow)] backdrop-blur-sm ring-1 ring-white/15">
              <Boxes className="h-6 w-6 text-white" />
            </div>
            <div className="text-xl font-bold tracking-tight text-white">
              FTL
            </div>
          </motion.div>

          <motion.div
            variants={stagger}
            initial={reducedMotion ? false : "hidden"}
            animate="visible"
            className="max-w-lg"
          >
            <motion.p
              variants={staggerItem}
              className="text-xs font-medium uppercase tracking-[0.18em] text-brand-200/80"
            >
              Felix · AI Program Manager
            </motion.p>
            <motion.h2
              variants={staggerItem}
              className="mt-3 text-3xl font-bold leading-tight text-white xl:text-4xl"
            >
              {illustrationTagline}
            </motion.h2>
            <motion.p
              variants={staggerItem}
              className="mt-4 text-sm leading-relaxed text-slate-200/80"
            >
              Full software development lifecycle with AI personas. From
              objective decomposition to production deployment — every step
              captured, traced, and baseline-verified.
            </motion.p>
          </motion.div>

          <motion.div
            variants={reveal}
            initial={reducedMotion ? false : "hidden"}
            animate="visible"
            className="flex items-center gap-6 text-xs text-slate-300/70"
          >
            <Link
              to="/legal/terms"
              className="hover:text-white transition-colors"
            >
              Terms
            </Link>
            <Link
              to="/legal/privacy"
              className="hover:text-white transition-colors"
            >
              Privacy
            </Link>
            <span>© 2026 FTL Development</span>
          </motion.div>
        </div>
      </div>

      {/* Right — form panel */}
      <div className="relative flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-800/40 px-4 py-10 sm:px-8 lg:px-12">
        <motion.div
          variants={stagger}
          initial={reducedMotion ? false : "hidden"}
          animate="visible"
          className="w-full max-w-md"
        >
          {/* Mobile logo (shown only when illustration is hidden) */}
          <motion.div
            variants={staggerItem}
            className="mb-8 flex items-center justify-center gap-2 lg:hidden"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-600 text-white shadow-[var(--shadow-glow)]">
              <Boxes className="h-5 w-5" />
            </div>
            <span className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
              FTL
            </span>
            {showBeta && (
              <span className="rounded-full border border-brand-300/60 bg-brand-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-700 dark:border-brand-700/40 dark:bg-brand-900/60 dark:text-brand-300">
                Beta
              </span>
            )}
          </motion.div>

          {/* Heading */}
          <motion.div variants={staggerItem} className="mb-7">
            {eyebrow && (
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-600 dark:text-brand-400">
                {eyebrow}
              </p>
            )}
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-3xl">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{subtitle}</p>
            )}
          </motion.div>

          {/* Form slot */}
          <motion.div
            variants={staggerItem}
            className={cn(
              "rounded-2xl border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900",
              "p-6 shadow-[var(--shadow-card)] backdrop-blur-sm sm:p-8",
            )}
          >
            {children}
          </motion.div>

          {/* Footer link */}
          {footer && (
            <motion.div
              variants={staggerItem}
              className="mt-6 text-center text-sm text-slate-600 dark:text-slate-300"
            >
              {footer}
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
